import * as THREE from 'three';
import type { Scene } from '../../scenes';
import { palette } from '../../visual';
import { Planet } from './Planet';
import type { PlanetOptions } from './Planet';
import { Moon } from './Moon';
import { Star } from './Star';
import { Station } from './Station';
import { Asteroid } from './Asteroid';
import type { AsteroidKind } from './Asteroid';
import type { Landable } from './Landable';

// Cumulative spawn weights for asteroid kinds. Tuned so:
//   gold    ~5%   — rare metallic nuggets
//   uranium ~12%  — fuel ore for the recycler, kept rare on purpose
//   plasma  ~10%  — magenta crystal, valuable
//   ice     ~15%  — cyan ionised ice
//   iron    ~30%  — common metallic backbone of the economy
//   quartz  ~28%  — abundant glassy crystals
// Every roll resolves to a typed kind so all visible rocks have ore — no
// more "barren" standard asteroids that confuse new players.
const KIND_GOLD_END = 0.05;
const KIND_URANIUM_END = KIND_GOLD_END + 0.12;
const KIND_PLASMA_END = KIND_URANIUM_END + 0.10;
const KIND_ICE_END = KIND_PLASMA_END + 0.15;
const KIND_IRON_END = KIND_ICE_END + 0.30;
// remaining ~28% → quartz

function rollAsteroidKind(): AsteroidKind {
  const roll = Math.random();
  if (roll < KIND_GOLD_END) return 'gold';
  if (roll < KIND_URANIUM_END) return 'uranium';
  if (roll < KIND_PLASMA_END) return 'plasma';
  if (roll < KIND_ICE_END) return 'ice';
  if (roll < KIND_IRON_END) return 'iron';
  return 'quartz';
}

export const PLAYER_SPAWN = new THREE.Vector3(0, 0, 2400);
export const SYSTEM_CENTER = new THREE.Vector3(0, 0, 0);

interface PlanetLayoutEntry {
  readonly radius: number;
  readonly colors: {
    readonly base: number;
    readonly accent: number;
    readonly detail: number;
    readonly halo: number;
    readonly clouds: number;
  };
  readonly orbit: {
    readonly radius: number;
    readonly speed: number;
    readonly angle: number;
    readonly y: number;
  };
  readonly spinSpeed: number;
  readonly axialTilt?: number;
  readonly cloudDensity?: number;
  readonly displayName: string;
  readonly ring?: PlanetOptions['ring'];
  readonly moon?: {
    readonly radius: number;
    readonly distance: number;
    readonly colors: {
      readonly base: number;
      readonly accent: number;
      readonly detail: number;
      readonly halo: number;
      readonly clouds: number;
    };
    readonly orbitSpeed?: number;
    readonly orbitTilt?: number;
    readonly initialAngle?: number;
    readonly displayName: string;
  };
  readonly orbitalBelt?: {
    readonly count: number;
    readonly radius: number;
    readonly spread: number;
    readonly tilt: number;
    readonly sizeBase: number;
  };
}

/** Definition for a respawnable asteroid pool (a planet ring or orbital belt).
 *  The pool tracks a live anchor (the planet) so its asteroids — both at
 *  spawn time and on respawn — orbit the parent body around the star instead
 *  of getting stranded in static world-space coordinates. */
export interface AsteroidPoolDef {
  readonly id: string;
  readonly count: number;
  readonly anchor: Planet;
  readonly innerRadius: number;
  readonly outerRadius: number;
  readonly tilt: number;
  readonly verticalSpread: number;
  readonly sizeBase: number;
}

// Planets sized realistically against the player ship (~4u long): rocky
// inner planets ~190-280u, gas giants up to ~540u. Orbits pushed apart so
// rings/belts/moons of adjacent planets don't intersect.
const PLANET_LAYOUT: readonly PlanetLayoutEntry[] = [
  {
    radius: 200,
    colors: palette.planets.rose,
    orbit: { radius: 3500, speed: 0.0058, angle: 2.023, y: 160 },
    spinSpeed: 0.054,
    axialTilt: 0.5,
    cloudDensity: 0.55,
    displayName: 'Caelis'
  },
  {
    radius: 360,
    colors: palette.planets.mint,
    orbit: { radius: 6500, speed: 0.0044, angle: -0.749, y: -260 },
    spinSpeed: 0.028,
    axialTilt: 0.18,
    cloudDensity: 0.9,
    displayName: 'Verdia',
    moon: {
      radius: 70,
      distance: 900,
      colors: palette.planets.butter,
      orbitSpeed: 0.03,
      orbitTilt: 0.4,
      initialAngle: 1.1,
      displayName: 'Verdia Minor'
    },
    orbitalBelt: {
      // Tutorial gathering target — density bumped so a fresh player
      // has plenty to chew through while building cargo for the
      // mining-tool purchases.
      count: 88,
      radius: 1100,
      spread: 110,
      tilt: 0.25,
      sizeBase: 7.0
    }
  },
  {
    radius: 280,
    colors: palette.planets.peach,
    orbit: { radius: 9500, speed: 0.0034, angle: -2.749, y: 380 },
    spinSpeed: 0.036,
    axialTilt: 0.32,
    cloudDensity: 0.7,
    displayName: 'Ambre',
    ring: { inner: 410, outer: 670, tilt: 0.42, opacity: 0.55 }
  },
  {
    radius: 270,
    colors: palette.planets.butter,
    orbit: { radius: 13000, speed: 0.0024, angle: 0.216, y: -540 },
    spinSpeed: 0.046,
    axialTilt: 0.22,
    cloudDensity: 0.4,
    displayName: 'Auron',
    orbitalBelt: {
      count: 36,
      radius: 670,
      spread: 50,
      tilt: 0.5,
      sizeBase: 6.2
    }
  },
  {
    radius: 540,
    colors: palette.planets.lavender,
    orbit: { radius: 17500, speed: 0.0017, angle: 1.298, y: 720 },
    spinSpeed: 0.02,
    axialTilt: 0.12,
    cloudDensity: 1.0,
    displayName: 'Solenne',
    ring: { inner: 770, outer: 1240, tilt: 0.28, opacity: 0.42 },
    moon: {
      radius: 110,
      distance: 1390,
      colors: palette.planets.rose,
      orbitSpeed: 0.022,
      orbitTilt: -0.3,
      initialAngle: 2.7,
      displayName: 'Solenne Echo'
    },
    orbitalBelt: {
      count: 60,
      radius: 1730,
      spread: 130,
      tilt: -0.18,
      sizeBase: 9.0
    }
  },
  {
    radius: 190,
    colors: palette.planets.mint,
    orbit: { radius: 22000, speed: 0.0011, angle: 2.792, y: -780 },
    spinSpeed: 0.05,
    axialTilt: 0.3,
    cloudDensity: 0.85,
    displayName: 'Nyara',
    moon: {
      radius: 45,
      distance: 480,
      colors: palette.planets.peach,
      orbitSpeed: 0.04,
      orbitTilt: 0.55,
      initialAngle: 0.3,
      displayName: 'Nyara Whisper'
    }
  }
];

const STAR_CONFIG = {
  radius: 750,
  core: palette.star.gold,
  glow: palette.star.warm,
  corona: palette.star.pink,
  lightIntensity: 2.0
} as const;

const SPAWN_CLUSTER = {
  count: 14,
  minRadius: 80,
  maxRadius: 320,
  verticalSpread: 140
} as const;

// Main asteroid belt — sits in the gap between Auron (13000) and Solenne (17500).
const MAIN_BELT = {
  count: 280,
  innerRadius: 14500,
  outerRadius: 16000,
  verticalSpread: 480,
  tiltX: 0.05,
  tiltZ: -0.03,
  clusterCount: 9,
  clusterRadius: 280
} as const;

// Deep drift — sparse asteroids spread through the whole sector volume.
const DEEP_DRIFT = {
  count: 220,
  minRadius: 2500,
  maxRadius: 24000,
  verticalBias: 0.5
} as const;

const LANDMARKS: ReadonlyArray<{
  readonly offset: { readonly x: number; readonly y: number; readonly z: number };
  readonly size: number;
  readonly tint?: number;
  readonly elongation?: number;
}> = [
  { offset: { x: 130, y: 18, z: -150 }, size: 16, elongation: 0.35 },
  { offset: { x: -90, y: -28, z: -210 }, size: 13, tint: palette.asteroid.warm, elongation: 0.4 },
  { offset: { x: 60, y: 40, z: 290 }, size: 10, elongation: 0.25 },
  { offset: { x: -160, y: 10, z: -90 }, size: 11, tint: palette.asteroid.accent, elongation: 0.3 },
  { offset: { x: 240, y: -22, z: 60 }, size: 14, elongation: 0.38 }
];

export interface SectorBuildResult {
  readonly landables: readonly Landable[];
  readonly star: Star;
  readonly spawnPoint: THREE.Vector3;
  readonly stationPosition: THREE.Vector3;
  readonly station: Station;
  readonly asteroidCount: number;
  /** Number of asteroids that don't belong to any pool (deep drift / spawn cluster). */
  readonly globalAsteroidCount: number;
  readonly asteroidPools: readonly AsteroidPoolDef[];
}

export function buildSector(scene: Scene): SectorBuildResult {
  const landables: Landable[] = [];
  const pools: AsteroidPoolDef[] = [];

  const star = new Star({
    radius: STAR_CONFIG.radius,
    core: STAR_CONFIG.core,
    glow: STAR_CONFIG.glow,
    corona: STAR_CONFIG.corona,
    position: SYSTEM_CENTER,
    lightIntensity: STAR_CONFIG.lightIntensity
  });
  scene.entities.add(star);

  for (const entry of PLANET_LAYOUT) {
    // The capture zone must cover anything that "belongs" to the planet —
    // body, ring, orbital belt — so a ship parked anywhere in that zone
    // gets tugged along with the planet's orbit instead of being left behind.
    const ringOuter = entry.ring?.outer ?? 0;
    const beltOuter = entry.orbitalBelt
      ? entry.orbitalBelt.radius + entry.orbitalBelt.spread
      : 0;
    const captureRadius =
      Math.max(entry.radius * 2.5, ringOuter, beltOuter) + 80;

    const planet = new Planet({
      radius: entry.radius,
      base: entry.colors.base,
      accent: entry.colors.accent,
      detail: entry.colors.detail,
      halo: entry.colors.halo,
      clouds: entry.colors.clouds,
      orbit: {
        center: SYSTEM_CENTER,
        radius: entry.orbit.radius,
        speed: entry.orbit.speed,
        initialAngle: entry.orbit.angle,
        y: entry.orbit.y
      },
      spinSpeed: entry.spinSpeed,
      axialTilt: entry.axialTilt,
      cloudDensity: entry.cloudDensity,
      displayName: entry.displayName,
      ring: entry.ring,
      captureRadius
    });
    scene.entities.add(planet);
    landables.push(planet);

    if (entry.moon) {
      const moon = new Moon({
        radius: entry.moon.radius,
        base: entry.moon.colors.base,
        accent: entry.moon.colors.accent,
        detail: entry.moon.colors.detail,
        halo: entry.moon.colors.halo,
        clouds: entry.moon.colors.clouds,
        parent: planet,
        orbitDistance: entry.moon.distance,
        orbitSpeed: entry.moon.orbitSpeed,
        orbitTilt: entry.moon.orbitTilt,
        initialAngle: entry.moon.initialAngle,
        displayName: entry.moon.displayName
      });
      scene.entities.add(moon);
      landables.push(moon);
    }

    if (entry.ring) {
      const def = buildRingPool(entry.displayName, planet, entry.ring);
      pools.push(def);
      scatterRingAsteroids(scene, def);
    }

    if (entry.orbitalBelt) {
      const id = `belt:${entry.displayName}`;
      const inner = Math.max(1, entry.orbitalBelt.radius - entry.orbitalBelt.spread);
      const outer = entry.orbitalBelt.radius + entry.orbitalBelt.spread;
      const def: AsteroidPoolDef = {
        id,
        count: entry.orbitalBelt.count,
        anchor: planet,
        innerRadius: inner,
        outerRadius: outer,
        tilt: entry.orbitalBelt.tilt,
        verticalSpread: entry.orbitalBelt.spread * 0.4,
        sizeBase: entry.orbitalBelt.sizeBase
      };
      pools.push(def);
      scatterOrbitalBelt(scene, def);
    }
  }

  // Station is pushed well in front of the player spawn so the very
  // first mission ("reach the station") has a real, visible journey
  // — ~1500u at cruise speed = roughly 12-15 seconds of flight, with
  // enough lateral drift that the approach feels like piloting rather
  // than holding W. Spawn cluster + landmarks stay around the player.
  const stationPosition = new THREE.Vector3(
    PLAYER_SPAWN.x - 280,
    PLAYER_SPAWN.y + 80,
    PLAYER_SPAWN.z - 1500
  );
  const station = new Station({
    position: stationPosition,
    rotationY: Math.PI * 0.18
  });
  scene.entities.add(station);

  for (const landmark of LANDMARKS) {
    const kind = rollAsteroidKind();
    scene.entities.add(
      new Asteroid({
        position: new THREE.Vector3(
          PLAYER_SPAWN.x + landmark.offset.x,
          PLAYER_SPAWN.y + landmark.offset.y,
          PLAYER_SPAWN.z + landmark.offset.z
        ),
        size: landmark.size,
        detail: 3,
        tint: landmark.tint,
        elongation: landmark.elongation,
        kind
      })
    );
  }

  scatterSpawnCluster(scene);
  scatterMainBelt(scene);
  scatterDeepDrift(scene);

  let asteroidCount = 0;
  let globalAsteroidCount = 0;
  for (const entity of scene.entities.all().values()) {
    if (entity instanceof Asteroid) {
      asteroidCount++;
      if (entity.poolId === null) globalAsteroidCount++;
    }
  }

  return {
    landables,
    star,
    spawnPoint: PLAYER_SPAWN.clone(),
    stationPosition,
    station,
    asteroidCount,
    globalAsteroidCount,
    asteroidPools: pools
  };
}

/** Derive an asteroid pool from a planet's visible ring geometry. Density and
 *  rock size scale with the ring's area/thickness so any planet that gains
 *  a ring automatically becomes a real asteroid field — no per-planet config. */
function buildRingPool(
  planetName: string,
  planet: Planet,
  ring: NonNullable<PlanetOptions['ring']>
): AsteroidPoolDef {
  const area = Math.PI * (ring.outer * ring.outer - ring.inner * ring.inner);
  // ~1 asteroid per 6500 sq u of ring face — dense enough that flying through
  // the ring you constantly see rocks. Clamped so tiny rings still feel like
  // a belt and huge rings don't blow the entity budget.
  const count = Math.max(80, Math.min(260, Math.round(area / 6500)));

  const thickness = ring.outer - ring.inner;
  // Thin disc — most rocks ride the ring plane with a little vertical jitter.
  const verticalSpread = Math.max(8, thickness * 0.08);
  // Bigger rings carry chunkier rocks.
  const sizeBase = 4 + thickness / 130;

  return {
    id: `ring:${planetName}`,
    count,
    anchor: planet,
    innerRadius: ring.inner,
    outerRadius: ring.outer,
    tilt: ring.tilt ?? 0,
    verticalSpread,
    sizeBase
  };
}

function scatterOrbitalBelt(scene: Scene, def: AsteroidPoolDef): void {
  for (let i = 0; i < def.count; i++) {
    const angle = (i / def.count) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
    scene.entities.add(spawnPoolAsteroid(def, angle));
  }
}

/** Scatter rocks throughout a planet's visible ring. Tagged with the pool id
 *  so the respawner can replenish them within the same band when destroyed. */
function scatterRingAsteroids(scene: Scene, def: AsteroidPoolDef): void {
  for (let i = 0; i < def.count; i++) {
    const angle = (i / def.count) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
    scene.entities.add(spawnPoolAsteroid(def, angle));
  }
}

const _poolOffset = new THREE.Vector3();
const _poolTilt = new THREE.Euler();
const _poolCenter = new THREE.Vector3();

/** Returns the pool's current world center — i.e. the anchor planet's live
 *  position, so respawning rocks land in the *current* ring, not where the
 *  ring was when the sector was first built. */
export function getPoolCenter(
  def: AsteroidPoolDef,
  out: THREE.Vector3
): THREE.Vector3 {
  return def.anchor.getWorldPosition(out);
}

/** Build a single asteroid placed within a pool's annular band, tagged with
 *  its pool id so the respawner can route it back to the right ring, and
 *  anchored to the parent planet so it follows the orbit.
 *
 *  We mirror the exact transform the visual ring mesh uses in Planet.buildRing
 *  (RingGeometry on local XY, then `rotation.x = π/2 − tilt`, then the planet's
 *  axial-tilt quaternion via the parent-child relationship). That way the
 *  rocks lie *on* the visible ring instead of forming a second ring next to it. */
export function spawnPoolAsteroid(
  def: AsteroidPoolDef,
  angle = Math.random() * Math.PI * 2
): Asteroid {
  const radius =
    def.innerRadius + Math.random() * (def.outerRadius - def.innerRadius);
  const j = (Math.random() - 0.5) * def.verticalSpread * 2;

  // Same parameterization as RingGeometry: a flat ring on the local XY plane,
  // with an out-of-plane jitter on local Z so the band has a little thickness.
  _poolOffset.set(Math.cos(angle) * radius, Math.sin(angle) * radius, j);

  // Same rotation Planet.buildRing applies to the visual ring mesh.
  _poolTilt.set(Math.PI / 2 - def.tilt, 0, 0);
  _poolOffset.applyEuler(_poolTilt);

  // Inherit the planet's local→world rotation (axial tilt) — the visual ring
  // gets this for free as a child of planet.object3D; we have to apply it
  // manually because asteroid object3Ds live at the scene root.
  _poolOffset.applyQuaternion(def.anchor.object3D.quaternion);

  const sizeRoll = Math.random();
  const size =
    sizeRoll < 0.12
      ? def.sizeBase * 1.8 + Math.random() * def.sizeBase
      : def.sizeBase * 0.6 + Math.random() * def.sizeBase * 1.2;

  const tintRoll = Math.random();
  const tint =
    tintRoll < 0.1
      ? palette.asteroid.accent
      : tintRoll < 0.18
        ? palette.asteroid.warm
        : palette.asteroid.body;

  const kind = rollAsteroidKind();
  getPoolCenter(def, _poolCenter);
  return new Asteroid({
    // Ring/belt rocks are dense but small on-screen — detail=1 (icosahedron
    // base resolution) is plenty once craters and noise displace it. Saves
    // ~75% of vertices vs detail=2, which adds up at 200+ rocks per ring.
    position: _poolCenter.clone().add(_poolOffset),
    size,
    detail: size > 8 ? 2 : 1,
    tint,
    kind,
    poolId: def.id,
    anchor: def.anchor
  });
}

function randomTint(): number {
  const roll = Math.random();
  if (roll < 0.1) return palette.asteroid.accent;
  if (roll < 0.22) return palette.asteroid.warm;
  return palette.asteroid.body;
}

function rollSize(smallBias = 0.72): number {
  const r = Math.random();
  if (r < 0.05) return 8 + Math.random() * 6;
  if (r < 0.05 + (1 - smallBias) * 0.7) return 4 + Math.random() * 3;
  return 1.4 + Math.random() * 2.6;
}

function scatterSpawnCluster(scene: Scene): void {
  const { count, minRadius, maxRadius, verticalSpread } = SPAWN_CLUSTER;

  for (let i = 0; i < count; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    const r = minRadius + Math.random() * (maxRadius - minRadius);

    const x = PLAYER_SPAWN.x + r * Math.sin(phi) * Math.cos(theta);
    const y = PLAYER_SPAWN.y + (Math.random() - 0.5) * verticalSpread;
    const z = PLAYER_SPAWN.z + r * Math.sin(phi) * Math.sin(theta);

    const size = 1.3 + Math.random() * 2.4;
    const kind = rollAsteroidKind();
    scene.entities.add(
      new Asteroid({
        position: new THREE.Vector3(x, y, z),
        size,
        detail: 1,
        tint: randomTint(),
        kind
      })
    );
  }
}

function scatterMainBelt(scene: Scene): void {
  const {
    count,
    innerRadius,
    outerRadius,
    verticalSpread,
    tiltX,
    tiltZ,
    clusterCount,
    clusterRadius
  } = MAIN_BELT;

  const tiltEuler = new THREE.Euler(tiltX, 0, tiltZ);
  const offset = new THREE.Vector3();

  const clusters: { angle: number; radius: number }[] = [];
  for (let i = 0; i < clusterCount; i++) {
    clusters.push({
      angle: Math.random() * Math.PI * 2,
      radius: innerRadius + Math.random() * (outerRadius - innerRadius)
    });
  }

  for (let i = 0; i < count; i++) {
    const inCluster = Math.random() < 0.45 && clusters.length > 0;
    let angle: number;
    let radius: number;

    if (inCluster) {
      const seed = clusters[Math.floor(Math.random() * clusters.length)]!;
      const jitterR = (Math.random() - 0.5) * clusterRadius * 2;
      const jitterA = (Math.random() - 0.5) * 0.14;
      angle = seed.angle + jitterA;
      radius = seed.radius + jitterR;
    } else {
      angle = Math.random() * Math.PI * 2;
      radius = innerRadius + Math.random() * (outerRadius - innerRadius);
    }

    const y = gaussianNoise() * verticalSpread * 0.5;
    offset.set(Math.cos(angle) * radius, y, Math.sin(angle) * radius);
    offset.applyEuler(tiltEuler);

    const size = rollSize(0.68);
    const kind = rollAsteroidKind();
    scene.entities.add(
      new Asteroid({
        position: SYSTEM_CENTER.clone().add(offset),
        size,
        detail: size > 5 ? 2 : 1,
        tint: randomTint(),
        kind
      })
    );
  }
}

function scatterDeepDrift(scene: Scene): void {
  const { count, minRadius, maxRadius, verticalBias } = DEEP_DRIFT;

  for (let i = 0; i < count; i++) {
    const theta = Math.random() * Math.PI * 2;
    const u = Math.random() * 2 - 1;
    const sinPhi = Math.sqrt(1 - u * u);
    const r = minRadius + Math.pow(Math.random(), 0.6) * (maxRadius - minRadius);

    const x = r * sinPhi * Math.cos(theta);
    const y = r * u * verticalBias;
    const z = r * sinPhi * Math.sin(theta);

    const size = rollSize(0.82);
    scene.entities.add(
      new Asteroid({
        position: new THREE.Vector3(x, y, z),
        size,
        detail: size > 5 ? 2 : 1,
        tint: randomTint(),
        kind: rollAsteroidKind()
      })
    );
  }
}

function gaussianNoise(): number {
  const u = Math.max(1e-6, Math.random());
  const v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
